import { Component } from 'react';
import sharedStyles from '../single-page.module.scss';
import { FullscreenHost } from '../fullscreen-host.component';
import styles from './convert-der-to-pem.component.module.scss';

type Label =
    | 'CERTIFICATE'
    | 'PRIVATE KEY'
    | 'RSA PRIVATE KEY'
    | 'EC PRIVATE KEY'
    | 'PUBLIC KEY'
    | 'CERTIFICATE REQUEST';

interface State {
    fileName: string;
    derBytes: Uint8Array | null;
    label: Label;
    output: string;
    error: string | null;
    copied: boolean;
}

function derToPem(bytes: Uint8Array, label: Label): string {
    let bin = '';
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
    const b64 = btoa(bin);
    const wrapped = b64.match(/.{1,64}/g)?.join('\n') ?? '';
    return `-----BEGIN ${label}-----\n${wrapped}\n-----END ${label}-----\n`;
}

export default class ConvertDerToPemComponent extends Component<{ feature?: string }, State> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;
    state: State = {
        fileName: '',
        derBytes: null,
        label: 'CERTIFICATE',
        output: '',
        error: null,
        copied: false,
    };

    componentWillUnmount() {
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
    }

    onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0];
        if (!f) return;
        const reader = new FileReader();
        reader.onload = () => {
            const bytes = new Uint8Array(reader.result as ArrayBuffer);
            const output = derToPem(bytes, this.state.label);
            this.setState({ derBytes: bytes, fileName: f.name, output, error: null });
        };
        reader.onerror = () => this.setState({ error: 'Failed to read file' });
        reader.readAsArrayBuffer(f);
    };

    onLabelChange = (label: Label) => {
        this.setState({ label });
        if (this.state.derBytes) {
            this.setState({ output: derToPem(this.state.derBytes, label) });
        }
    };

    copyOutput = () => {
        if (!this.state.output) return;
        navigator.clipboard.writeText(this.state.output).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copied: true });
            this.copyTimeout = setTimeout(() => this.setState({ copied: false }), 2000);
        });
    };

    render() {
        const { fileName, derBytes, label, output, error, copied } = this.state;
        return (
            <FullscreenHost>
            <div className="row">
                <div className="col-12 col-md-6">
                    <label className={sharedStyles.label}>DER file (binary)</label>
                    <div className={styles.fileBox}>
                        <input type="file" id="der-file" accept=".der,.cer,.crt,.key"
                            onChange={this.onFileChange} className={styles.fileInput} />
                        <label htmlFor="der-file" className={sharedStyles.buttonPrimary}>Choose file</label>
                        {fileName && <div className={styles.fileName}>{fileName} ({derBytes?.length ?? 0} bytes)</div>}
                    </div>
                    <label className={sharedStyles.label}>
                        PEM label
                        <select className={styles.input} value={label}
                            onChange={e => this.onLabelChange(e.target.value as Label)}>
                            <option value="CERTIFICATE">CERTIFICATE</option>
                            <option value="CERTIFICATE REQUEST">CERTIFICATE REQUEST</option>
                            <option value="PRIVATE KEY">PRIVATE KEY (PKCS#8)</option>
                            <option value="RSA PRIVATE KEY">RSA PRIVATE KEY (PKCS#1)</option>
                            <option value="EC PRIVATE KEY">EC PRIVATE KEY</option>
                            <option value="PUBLIC KEY">PUBLIC KEY</option>
                        </select>
                    </label>
                    {error && <p className={styles.error} role="alert">{error}</p>}
                </div>
                <div className="col-12 col-md-6">
                    <label className={sharedStyles.label}>PEM output</label>
                    <textarea className={`${sharedStyles.textarea} ${styles.mono}`} value={output} readOnly rows={14}
                        placeholder="Upload a .der file on the left to see PEM output." />
                    <button className={`${sharedStyles.copyButton} ${copied ? sharedStyles.copyButtonSuccess : ''}`}
                        onClick={this.copyOutput} disabled={!output}>
                        {copied ? '✓ Copied' : 'Copy PEM'}
                    </button>
                </div>
            </div>

            </FullscreenHost>
        );
    }
}
